// Helper murni untuk fitur Pengaturan Ekspedisi (expeditions).
//
// Sengaja BEBAS dari Supabase/React supaya gampang diuji (lihat
// src/lib/__tests__/expeditions.test.ts). Data-layer ada di
// src/lib/supabase/expeditions.ts; UI di
// src/components/admin/AdminExpeditions.tsx + ExpeditionSelect.tsx.
//
// Ekspedisi = daftar kurir bernama (JNE, J&T, …) yang dikelola admin. Dua
// "ekspedisi khusus" — Ambil di Tempat & Ongkir COD — adalah KONSTANTA bawaan
// di sini (selalu tersedia, tak bisa terhapus) dan TIDAK disimpan di DB.
// Tarif/ongkir tetap di-input manual di alur pengiriman.

import {
  BITESHIP_COURIERS,
  courierLabel,
  isCacheableServiceType,
  type CourierCatalogEntry,
} from "@/lib/biteship";

export type Expedition = {
  id: string;
  name: string;
  isActive: boolean;
  sortOrder: number;
  createdAt: string | null;
  /**
   * Kode kurir Biteship (mig 3720) — jembatan ke hasil cek ongkir otomatis.
   * `null` = ekspedisi manual: tetap bisa dipilih di alur pengiriman, hanya
   * tak ikut dicocokkan dengan tarif. Dua ekspedisi khusus bawaan ("Ambil di
   * Tempat", "Ongkir COD") memang tak punya padanan dan selalu null.
   *
   * Tanpa kolom ini pencocokan terpaksa lewat NAMA yang diketik admin ("J&T",
   * "JNT Express", "J & T") melawan `courier_code` Biteship (`jnt`) — rapuh,
   * dan salah cocok berarti tarif kurir LAIN yang tampil sebagai harga.
   */
  biteshipCourierCode: string | null;
  /**
   * Kode LAYANAN Biteship (mig 3950) — satu tingkat lebih dalam dari
   * `biteshipCourierCode`: "reg", "ons", "eko", "reg_pack", "idtruck".
   *
   * `null` = baris ini mewakili kurirnya SECARA UMUM, perilaku sebelum mig
   * 3900 dan tetap sah. Gunanya muncul saat satu kode kurir dipakai lebih dari
   * satu baris — TIKI REG / TIKI ONS / TIKI ECO semuanya `tiki` — karena tanpa
   * kolom ini seluruh tarif TIKI mendarat pada satu baris yang sama (yang
   * `sortOrder`-nya terkecil), sehingga pembeli memilih REG tapi yang tercatat
   * di `shipment_requests.expedition` adalah ECO: layanan lain, harga lain.
   *
   * Selalu ikut kurirnya. Mengganti `biteshipCourierCode` WAJIB mengosongkan
   * kolom ini — daftar layanan milik kurir yang lama tak berlaku untuk kurir
   * yang baru. Ekspedisi manual (tanpa kode kurir) selalu `null`.
   */
  biteshipServiceCode: string | null;
  /**
   * Logo khusus toko ini (`expeditions.icon_url`, mig 3890) — TIMPAAN atas
   * logo default platform (`biteship_couriers.icon_url`). `null`/kosong =
   * belum menimpa, jadi yang dipakai logo default kurirnya; tanpa keduanya UI
   * menampilkan chip inisial.
   *
   * Penamaannya sengaja sama dengan `product_brands.icon_url` supaya aturan
   * resolusinya terbaca lugas. Aturan lengkapnya di `expeditionLogo.ts`.
   */
  iconUrl: string | null;
};

/** Draft form (string mentah dari input) sebelum disimpan. */
export type ExpeditionDraft = {
  name: string;
  isActive: boolean;
  /** "" = tanpa kode kurir (disimpan `null`). */
  biteshipCourierCode: string;
  /**
   * "" = tanpa kode layanan (disimpan `null` = mewakili kurirnya secara umum).
   * Selalu ikut `biteshipCourierCode`: form WAJIB mengosongkannya saat kurir
   * diganti, karena daftar layanannya ikut berganti.
   */
  biteshipServiceCode: string;
};

/**
 * Ekspedisi khusus bawaan aplikasi — selalu muncul sebagai pilihan di alur
 * pengiriman, di samping ekspedisi yang dikonfigurasi admin.
 *   • "Ambil di Tempat" — pembeli mengambil sendiri (ongkir biasanya 0).
 *   • "Ongkir COD"      — bayar di tempat saat barang diterima.
 */
export const PICKUP_EXPEDITION = "Ambil di Tempat" as const;
export const COD_EXPEDITION = "Ongkir COD" as const;

export const SPECIAL_EXPEDITIONS: readonly string[] = [
  PICKUP_EXPEDITION,
  COD_EXPEDITION,
] as const;

/** True bila nama ekspedisi termasuk ekspedisi khusus bawaan (case-insensitive). */
export function isSpecialExpedition(name: string | null | undefined): boolean {
  return specialExpeditionKind(name) !== null;
}

export type SpecialExpeditionKind = "pickup" | "cod";

/**
 * Jenis ekspedisi khusus dari namanya (case-insensitive), atau `null` bila
 * ekspedisi biasa. Dipakai untuk menampilkan badge & perlakuan khusus.
 */
export function specialExpeditionKind(
  name: string | null | undefined
): SpecialExpeditionKind | null {
  const v = (name ?? "").trim().toLowerCase();
  if (!v) return null;
  if (v === PICKUP_EXPEDITION.toLowerCase()) return "pickup";
  if (v === COD_EXPEDITION.toLowerCase()) return "cod";
  return null;
}

/** Label badge ringkas untuk ekspedisi khusus (mis. di kartu/cetak). */
export function specialExpeditionBadge(
  kind: SpecialExpeditionKind
): string {
  return kind === "pickup" ? "AMBIL SENDIRI" : "COD";
}

// ── Ambil di Tempat TANPA alamat (permintaan pemilik 2026-10-05) ────────────
//
// Layar kirim ADMIN (Request Kirim, Nota + Kirim) tak lagi menanyakan alamat
// untuk paket yang diambil sendiri. Potret PROD 2026-10-05: dari ±440 paket
// Ambil di Tempat, ±400 beralamat teks pengisi asal lolos ("Diambil", "Galea",
// "Nitip") atau alamat rumah pembeli yang terpilih otomatis. Primitifnya di
// sini — modul murni yang sudah diimpor `cartActionShipmentForm` (Request
// Kirim) maupun `invoiceShipment` (Nota + Kirim) — supaya kedua layar tak
// memelihara salinan aturan yang sama.

/**
 * Isi `address_line` paket Ambil di Tempat yang dibuat tanpa alamat — nama
 * ekspedisinya sendiri, jadi label, surat jalan, dan detail pengiriman membaca
 * "Ambil di Tempat" di tempat alamat, bukan teks pengisi karangan admin.
 *
 * Sengaja BUKAN null: belum ada satu pun paket PROD tanpa `ship_address_line`
 * (0 dari 3.221, 2026-10-05), jadi hilirnya tak pernah diuji dengan alamat
 * kosong. Cabang admin RPC pengiriman (mig 4050) cukup menuntut baris ini.
 */
export const PICKUP_ADDRESS_LINE: string = PICKUP_EXPEDITION;

/**
 * Penerima paket Ambil di Tempat = pelanggan pemilik nota/keranjang
 * (keputusan pemilik 2026-10-05) — nama & HP dari profilnya, supaya label di
 * paket tetap menyebut siapa yang akan mengambilnya.
 */
export type PickupRecipient = {
  name?: string | null;
  phone?: string | null;
};

/**
 * "Ambil di Tempat" dipilih sebagai KURIR paket. Resi sendiri dikecualikan:
 * di sana nama itu hanya teks yang diketik ke kolom nama kurir — kesalahan
 * yang ditegur validasi masing-masing layar, bukan pilihan.
 */
export function isPickupChoice(input: {
  expedition: string | null | undefined;
  selfResiMode?: boolean;
}): boolean {
  return !input.selfResiMode && specialExpeditionKind(input.expedition) === "pickup";
}

/**
 * KURIR BUTUH KECAMATAN TUJUAN (putusan pemilik 2026-10-29, redesain langkah
 * Pengiriman). Tanpa kecamatan, pilihan kirim tinggal dua: "Ambil di Tempat"
 * dan "Punya Resi Sendiri" — keduanya tak pernah dikutip dari tujuan.
 *
 * Kalimatnya dipakai bersama layar admin & pembeli → tanpa kata ganti.
 */
export const NO_DESTINATION_EXPEDITION_MESSAGE =
  "Kurir butuh kecamatan tujuan. Isi kecamatannya, atau pilih Ambil di Tempat atau Punya Resi Sendiri.";

/** Pilihan kirim yang sah walau kecamatan tujuan belum diketahui. */
export function expeditionWorksWithoutDestination(input: {
  expedition: string | null | undefined;
  selfResiMode?: boolean;
}): boolean {
  return !!input.selfResiMode || isPickupChoice(input);
}

/**
 * Pesan penolakan bila ekspedisi terpilih butuh kecamatan yang belum ada, atau
 * null. Ekspedisi KOSONG bukan urusan fungsi ini — "pilih ekspedisi dulu"
 * sudah ditegur aturan masing-masing layar, dan dua pesan untuk satu kolom
 * kosong hanya membingungkan.
 *
 * Gerbangnya di validasi, bukan hanya di layar: kartu kurir memang
 * disembunyikan tanpa kecamatan, tapi pilihan yang sudah terlanjur dibuat
 * (lalu alamatnya diganti, atau draft lama dipulihkan) tetap tinggal di state.
 */
export function expeditionDestinationError(input: {
  expedition: string | null | undefined;
  selfResiMode?: boolean;
  hasDestination: boolean;
}): string | null {
  if (input.hasDestination) return null;
  if (!(input.expedition ?? "").trim() && !input.selfResiMode) return null;
  return expeditionWorksWithoutDestination(input)
    ? null
    : NO_DESTINATION_EXPEDITION_MESSAGE;
}

/**
 * Bagian alamat payload RPC untuk paket Ambil di Tempat tanpa alamat. Nama/HP
 * yang kosong dibiarkan `undefined` — label melewati barisnya (idiom alamat
 * tempel mig 209), dan mengarangnya berarti mencetak orang yang salah.
 */
export function pickupAddressInput(recipient?: PickupRecipient | null): {
  recipientName?: string;
  phone?: string;
  addressLine: string;
} {
  return {
    recipientName: recipient?.name?.trim() || undefined,
    phone: recipient?.phone?.trim() || undefined,
    addressLine: PICKUP_ADDRESS_LINE
  };
}

/**
 * Alamat paket / niat kirim masih PENANDA Ambil di Tempat, bukan alamat
 * sungguhan.
 *
 * Yang dinilai BARIS TERAKHIR teks alamat, bukan seluruh teksnya: sheet
 * "Ubah Alamat" niat kirim melebur nama · HP ke baris pertama teks copas
 * (`composeIntentAddressText`), dan yang tersisa sesudah disimpan berbunyi
 * "Bu Rina · 0812…\nAmbil di Tempat". Alamat tersimpan (`addressId`) tak
 * pernah penanda.
 */
export function isPickupPlaceholderAddress(input: {
  addressId: string | null | undefined;
  addressLine: string | null | undefined;
}): boolean {
  if (input.addressId) return false;
  const lines = (input.addressLine ?? "")
    .split("\n")
    .map((l) => l.trim())
    .filter(Boolean);
  return (
    lines.length > 0 &&
    lines[lines.length - 1].toLowerCase() === PICKUP_ADDRESS_LINE.toLowerCase()
  );
}

/**
 * Penahan GANTI EKSPEDISI pada paket / niat kirim yang alamatnya masih
 * penanda Ambil di Tempat. null = boleh disimpan.
 *
 * Tanpa ini, paket yang tadinya diambil sendiri lalu diganti ke kurir (Set
 * Ongkir, Kirim Pesanan, ganti ekspedisi di detail nota) berangkat dengan
 * label beralamat "Ambil di Tempat" — dan banner AMBIL SENDIRI-nya ikut
 * hilang karena banner itu dibaca dari ekspedisi. "Ongkir COD" ikut ditahan:
 * barangnya juga diantar ke alamat pembeli.
 *
 * Ekspedisi kosong TIDAK ditahan — tak ada kurir yang akan membaca alamatnya.
 *
 * @param howToFix jalan keluar di layar pemanggil, mis. "isi alamatnya dulu
 *   lewat tombol Ganti Alamat".
 */
export function pickupPlaceholderCourierError(input: {
  addressId: string | null | undefined;
  addressLine: string | null | undefined;
  nextExpedition: string | null | undefined;
  howToFix: string;
}): string | null {
  if (!isPickupPlaceholderAddress(input)) return null;
  const next = (input.nextExpedition ?? "").trim();
  if (!next || specialExpeditionKind(next) === "pickup") return null;
  return `Paket ini tercatat ${PICKUP_EXPEDITION} tanpa alamat tujuan — ${input.howToFix} sebelum memilih ${next}.`;
}

/**
 * Kunci pilihan untuk KARTU ekspedisi khusus di daftar tarif (`RateOptions`).
 *
 * Sejak dropdown ekspedisi dilepas dari layar Request Kirim & Nota + Kirim,
 * daftar tarif adalah SATU-SATUNYA tempat ekspedisi dipilih — dan dua
 * ekspedisi khusus ini bukan tarif Biteship, jadi mereka takkan pernah datang
 * sebagai `ShippingRate`. Tanpa kartunya sendiri, "Ambil di Tempat" berhenti
 * bisa dipilih sama sekali, dan di nota admin itu ikut menutup satu-satunya
 * jalan sah menuju ongkir Rp 0 (`comboOngkirZeroAllowed`).
 *
 * Berawalan `special:` supaya tak pernah bertabrakan dengan `rateKey` (kurir)
 * maupun `manualRateKey` (tarif toko) — ketiganya dibandingkan sebagai satu
 * `selectedKey` di komponen yang sama.
 */
export function specialRateKey(name: string | null | undefined): string {
  return `special:${(name ?? "").trim().toLowerCase()}`;
}

/**
 * Kunci kartu "Punya Resi Sendiri" di daftar tarif (mig 4040).
 *
 * Berdiri sendiri, BUKAN `specialRateKey("Punya Resi Sendiri")`: dua ekspedisi
 * khusus adalah NAMA ekspedisi yang benar-benar tersimpan di
 * `shipment_requests.expedition`, sedangkan kartu ini bukan nama kurir sama
 * sekali — ia memilih CARA, dan nama kurirnya baru diketik sesudahnya. Kalau
 * kuncinya diturunkan dari nama, satu-satunya cara membedakan keduanya adalah
 * menaruh string "Punya Resi Sendiri" ke kolom ekspedisi — persis nilai palsu
 * yang akan tercetak di label kurir.
 */
export const SELF_RESI_RATE_KEY = "self-resi" as const;

/**
 * Susun daftar pilihan ekspedisi untuk dropdown pengiriman: ekspedisi khusus
 * bawaan + ekspedisi AKTIF yang dikonfigurasi admin (urut sortOrder lalu nama),
 * dedupe case-insensitive. Bila `current` (ekspedisi yang sudah tersimpan di
 * shipment) tak ada di daftar — mis. dibuat saat masih free-text atau sudah
 * dinonaktifkan — ia tetap disertakan agar nilai lama tidak hilang.
 */
export function buildExpeditionOptions(
  active: Expedition[],
  current?: string | null
): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  const push = (raw: string | null | undefined) => {
    const name = (raw ?? "").trim();
    if (!name) return;
    const key = name.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    out.push(name);
  };

  SPECIAL_EXPEDITIONS.forEach(push);
  [...active]
    .filter((e) => e.isActive)
    .sort(
      (a, b) =>
        a.sortOrder - b.sortOrder ||
        a.name.localeCompare(b.name, "id", { sensitivity: "base" })
    )
    .forEach((e) => push(e.name));
  push(current);

  return out;
}

/**
 * Validasi draft sebelum simpan. Mengembalikan pesan error (Bahasa Indonesia)
 * bila tidak valid, atau `null` bila lolos. `existing` dipakai untuk mendeteksi
 * nama duplikat (case-insensitive), kecuali baris yang sedang diedit (`editId`).
 */
export function validateExpeditionDraft(
  draft: ExpeditionDraft,
  existing: Expedition[] = [],
  editId?: string
): string | null {
  const name = draft.name.trim();
  if (!name) {
    return "Nama ekspedisi wajib diisi.";
  }
  if (isSpecialExpedition(name)) {
    return `"${name}" adalah ekspedisi khusus bawaan — tidak perlu dibuat.`;
  }
  const dup = existing.some(
    (e) => e.id !== editId && e.name.trim().toLowerCase() === name.toLowerCase()
  );
  if (dup) {
    return `Ekspedisi "${name}" sudah ada.`;
  }
  return null;
}

/**
 * Kode kurir mentah dari dropdown → bentuk yang disimpan DB.
 *
 * `null` (bukan `""`) untuk "tanpa kode": kolomnya nullable dan seluruh sisi
 * baca memperlakukan null sebagai "ekspedisi manual". Menyimpan string kosong
 * membuat dua nilai berbeda berarti hal yang sama, dan `couriersKey` maupun
 * pencocokan tarif akan kebagian entri hantu yang tak cocok apa pun.
 */
export function normalizeCourierCode(raw: string | null | undefined): string | null {
  const code = (raw ?? "").trim().toLowerCase();
  return code === "" ? null : code;
}

/**
 * Kode LAYANAN mentah dari dropdown → bentuk yang disimpan DB (mig 3950).
 *
 * Normalisasinya identik `normalizeCourierCode` (trim + lower + `null` bila
 * kosong) dan sengaja dipanggil lewat nama sendiri: kedua kolom berasal dari
 * ruang nilai yang berbeda — `tiki` vs `reg_pack` — dan tempat pemanggilannya
 * jadi terbaca tanpa harus mengingat bahwa keduanya kebetulan diperlakukan
 * sama.
 */
export function normalizeServiceCode(raw: string | null | undefined): string | null {
  return normalizeCourierCode(raw);
}

/** Buat draft kosong. */
export function emptyExpeditionDraft(): ExpeditionDraft {
  return {
    name: "",
    isActive: true,
    biteshipCourierCode: "",
    biteshipServiceCode: "",
  };
}

/** Isi draft dari ekspedisi yang sudah ada (untuk mode edit). */
export function draftFromExpedition(exp: Expedition): ExpeditionDraft {
  return {
    name: exp.name,
    isActive: exp.isActive,
    biteshipCourierCode: exp.biteshipCourierCode ?? "",
    biteshipServiceCode: exp.biteshipServiceCode ?? "",
  };
}

// ─── Kategori: Otomatis vs Manual ───────────────────────────────────────────

/**
 * Dua jenis ekspedisi yang perilakunya berbeda TOTAL, dan karena itu tampil
 * sebagai dua bagian terpisah di Pengaturan Ekspedisi:
 *
 *   • `otomatis` — ada di katalog Biteship. Ongkirnya datang dari cek ongkir,
 *     logonya otomatis dari `biteship_couriers.icon_url`.
 *   • `manual`   — buatan admin sendiri (NCS, BARAKA, INDAH CARGO). Tak ada di
 *     katalog, ongkirnya diketik admin, logonya diunggah sendiri per baris.
 */
export type ExpeditionKind = "otomatis" | "manual";

/**
 * Kategori sebuah ekspedisi. Otomatis = punya kode kurir Biteship, manual =
 * tidak. Sesederhana itu — JANGAN menebak dari nama.
 *
 * Menebak dari nama adalah kerapuhan yang persis dihilangkan oleh kolom kode
 * (mig 3720): "NCS" dan "ID STD" sama-sama tak menyebut kurirnya, dan yang
 * membedakan keduanya cuma ada-tidaknya `biteship_courier_code`.
 */
export function expeditionKind(exp: Expedition): ExpeditionKind {
  return normalizeCourierCode(exp.biteshipCourierCode) === null
    ? "manual"
    : "otomatis";
}

/**
 * Pisah satu daftar ekspedisi jadi dua kategori. Urutan di dalam masing-masing
 * DIPERTAHANKAN apa adanya dari `list` (yang sudah urut `sortOrder` lalu nama
 * dari data-layer), dan input TIDAK dimutasi.
 *
 * ⚠️ Baris yang bernama sama dengan EKSPEDISI KHUSUS bawaan ("Ambil di Tempat",
 * "Ongkir COD") tidak masuk kategori mana pun. Keduanya konstanta TS tanpa
 * baris DB (lihat `SPECIAL_EXPEDITIONS`) dan sudah tampil di kartunya sendiri;
 * baris DB bernama sama hanya bisa lahir dari sebelum `validateExpeditionDraft`
 * menolaknya, dan menampilkannya sebagai "manual" berarti dua hal berbeda
 * memakai satu nama di layar yang sama. Ia tidak ikut hilang: pemanggil yang
 * ingin membereskannya membandingkan panjang hasil dengan panjang input —
 * itulah yang dilakukan halaman admin.
 */
export function groupExpeditions(list: ReadonlyArray<Expedition>): {
  otomatis: Expedition[];
  manual: Expedition[];
} {
  const otomatis: Expedition[] = [];
  const manual: Expedition[] = [];
  for (const exp of list) {
    if (isSpecialExpedition(exp.name)) continue;
    (expeditionKind(exp) === "otomatis" ? otomatis : manual).push(exp);
  }
  return { otomatis, manual };
}

/**
 * Kode kurir Biteship yang terwakili ekspedisi AKTIF — unik & terurut.
 *
 * Sejak mig 3980 inilah SATU-SATUNYA daftar kurir yang dipakai fitur cek
 * ongkir; `site_settings.shipping_couriers` tidak lagi menentukan apa pun
 * (kartu "Kurir Biteship" yang menulisnya sudah dihapus). Yang tampil sebagai
 * pilihan pengiriman dan yang ditanyakan tarifnya kini benda yang sama.
 *
 * Terurut, bukan sekadar unik: sisi server memakainya menyusun `couriersKey`
 * yang masuk KUNCI cache tarif bersama-semua-toko, dan urutan yang bergantung
 * pada urutan baris DB memecah cache jadi dua baris untuk himpunan yang sama.
 */
export function activeCourierCodes(
  list: ReadonlyArray<Expedition>
): string[] {
  const codes = new Set<string>();
  for (const e of list) {
    if (!e.isActive) continue;
    const code = normalizeCourierCode(e.biteshipCourierCode);
    if (code !== null) codes.add(code);
  }
  return Array.from(codes).sort();
}

/**
 * Ada minimal satu ekspedisi OTOMATIS yang aktif? Gerbang "cek ongkir bisa
 * menyala": tanpa satu pun kurir, permintaan tarif dijawab `{enabled:false}`,
 * jadi layar yang menampilkan tombolnya akan berjanji sesuatu yang mustahil.
 */
export function hasActiveAutomaticExpedition(
  list: ReadonlyArray<Expedition>
): boolean {
  return activeCourierCodes(list).length > 0;
}

/**
 * Ada ekspedisi MANUAL yang aktif? Gerbang "layar ini perlu menanyakan tarif
 * sama sekali" sejak mig 3990.
 *
 * Tarif manual dihitung dari zona & tangga milik toko, tanpa menyentuh
 * Biteship — jadi toko yang cek ongkir otomatisnya MATI tetap punya tarif yang
 * layak ditampilkan. Gerbang yang hanya membaca syarat Biteship akan
 * menyembunyikan tombolnya dari toko yang justru paling membutuhkannya: yang
 * mengantar dengan kurirnya sendiri.
 *
 * Sengaja TIDAK memeriksa apakah zonanya benar-benar ada — tabel `zona` hanya
 * bisa dibaca admin (RLS mig 3990), sementara gerbang ini juga dipakai sisi
 * pembeli. Konsekuensi salah-tebaknya murah dan senyap: satu permintaan tarif
 * yang memulangkan daftar manual kosong, lalu `RateOptions` tidak merender
 * apa-apa untuk pembeli.
 */
export function hasActiveManualExpedition(
  list: ReadonlyArray<Expedition>
): boolean {
  return list.some(
    (e) =>
      e.isActive &&
      normalizeCourierCode(e.biteshipCourierCode) === null &&
      e.name.trim() !== "" &&
      !isSpecialExpedition(e.name)
  );
}

// ─── Pilihan dropdown Pengaturan Ekspedisi ──────────────────────────────────

export type ExpeditionCourierOption = { code: string; label: string };

/**
 * Pilihan KURIR untuk dropdown di Pengaturan Ekspedisi.
 *
 * Sumbernya KATALOG (`biteship_couriers`, mig 3760) bila sudah pernah
 * disinkronkan, bukan konstanta `BITESHIP_COURIERS`. Bedanya nyata: katalog
 * PROD memuat 22 kurir termasuk `jntcargo`, sementara konstantanya 15 dan tak
 * punya kode itu — sehingga baris "JNT CARGO" yang kodenya sudah benar
 * tergambar di layar sebagai "— manual —", persis kebalikan dari keadaannya.
 * Konstanta tetap dipakai sebagai cadangan untuk toko yang belum pernah
 * memuat katalog; tanpa itu dropdown-nya kosong sama sekali.
 *
 * Kode yang SEDANG TERSIMPAN selalu ikut dipulangkan walau tak dikenal
 * keduanya. Sebuah `<select>` yang nilainya tak ada di antara `<option>`-nya
 * menampilkan opsi pertama — jadi menyembunyikannya bukan "menyembunyikan yang
 * aneh", melainkan MEMBOHONGI admin tentang apa yang tersimpan.
 *
 * Urutan sumber dipertahankan apa adanya: katalog urut kode, konstanta urut
 * kelaziman pakai di Indonesia (lihat headernya) — abjad memaksa admin
 * memindai seluruh daftar untuk menemukan JNE.
 */
export function buildExpeditionCourierOptions(
  catalog: ReadonlyArray<CourierCatalogEntry> | null | undefined,
  current: string | null | undefined
): ExpeditionCourierOption[] {
  const out: ExpeditionCourierOption[] = [];
  const seen = new Set<string>();
  const push = (rawCode: string | null | undefined, label: string) => {
    const code = normalizeCourierCode(rawCode);
    if (code === null || seen.has(code)) return;
    seen.add(code);
    out.push({ code, label: label.trim() || courierLabel(code) });
  };

  const rows = catalog ?? [];
  if (rows.length > 0) {
    for (const entry of rows) push(entry.code, entry.name);
  } else {
    for (const c of BITESHIP_COURIERS) push(c.code, c.name);
  }
  push(current, courierLabel(current));

  return out;
}

export type ExpeditionServiceOption = {
  code: string;
  label: string;
  /**
   * `false` = jenis layanan yang TIDAK ikut cek ongkir (instant/same-day,
   * `UNSUPPORTED_SERVICE_TYPES`). Tetap boleh dipilih — kolom ini hanya soal
   * PELABELAN nama ekspedisi — tapi layar wajib mengatakannya, supaya admin
   * tak menunggu tarif yang memang tak akan pernah muncul.
   */
  cacheable: boolean;
};

/**
 * Pilihan LAYANAN untuk satu kode kurir, dibaca dari `biteship_couriers.services`
 * (JSONB, mig 3760).
 *
 * Kurir tanpa kode / tak ada di katalog → daftar kosong; layar menanganinya
 * dengan ajakan memuat katalog, bukan dropdown kosong tanpa penjelasan.
 *
 * Sama seperti kurirnya, kode layanan yang SEDANG TERSIMPAN selalu ikut walau
 * katalognya tak lagi mengenalnya — Biteship bisa mencabut sebuah layanan, dan
 * pemetaan yang tersimpan tidak boleh berubah arti diam-diam di layar.
 */
export function buildExpeditionServiceOptions(
  catalog: ReadonlyArray<CourierCatalogEntry> | null | undefined,
  courierCode: string | null | undefined,
  current: string | null | undefined
): ExpeditionServiceOption[] {
  const code = normalizeCourierCode(courierCode);
  if (code === null) return [];

  const out: ExpeditionServiceOption[] = [];
  const seen = new Set<string>();
  const entry = (catalog ?? []).find(
    (c) => normalizeCourierCode(c.code) === code
  );

  for (const svc of entry?.services ?? []) {
    const service = normalizeServiceCode(svc?.serviceCode);
    if (service === null || seen.has(service)) continue;
    seen.add(service);
    out.push({
      code: service,
      label: `${(svc.serviceName ?? "").trim() || service.toUpperCase()} (${service})`,
      cacheable: isCacheableServiceType(svc.serviceType),
    });
  }

  const cur = normalizeServiceCode(current);
  if (cur !== null && !seen.has(cur)) {
    // Tak dikenal katalog → dianggap layak-cache, cermin default
    // `isCacheableServiceType`: menuduh sebuah layanan tak didukung tanpa tahu
    // jenisnya lebih merusak daripada diam.
    out.push({ code: cur, label: `${cur.toUpperCase()} (${cur})`, cacheable: true });
  }

  return out;
}

/**
 * Kode kurir + kode LAYANAN Biteship → nama ekspedisi toko, atau `null` bila
 * tak ada yang cocok.
 *
 * Dipakai saat pengguna memilih satu tarif hasil cek ongkir: nilai yang
 * disimpan di `shipment_requests.expedition` adalah NAMA bebas milik toko
 * ("TIKI REG"), bukan kode Biteship ("tiki"), jadi tarif harus diterjemahkan
 * balik lewat `biteship_courier_code` (mig 3720) + `biteship_service_code`
 * (mig 3950).
 *
 * Urutan pencarian, dan urutannya WAJIB persis begini:
 *
 *   1. baris AKTIF dgn kode kurir cocok DAN kode layanan cocok  → paling tepat
 *   2. baris AKTIF dgn kode kurir cocok DAN layanan `null`      → wakil umum
 *   3. baris AKTIF dgn kode kurir cocok (`sortOrder` terkecil)  → perilaku lama
 *   4. `null` → pemanggil jatuh ke `courierLabel()`
 *
 * Langkah 3 dipertahankan supaya data lama tidak mendadak kehilangan nama;
 * yang berubah hanya ini: kalau layanannya DIKENAL, ia menang.
 *
 * ⚠️ Kenapa langkah 1 & 2 ada sama sekali. Sebelum mig 3950 fungsi ini cuma
 * punya langkah 3, dan bila BEBERAPA baris memakai kode kurir yang sama —
 * TIKI REG / TIKI ONS / TIKI ECO semuanya `tiki` — yang menang selalu
 * `sortOrder` terkecil. Jadi memilih tarif TIKI REG bisa menuliskan "TIKI ECO"
 * ke kiriman: layanan lain, harga lain, dan tertulis dengan percaya diri.
 * Sebelum kode kurir dipetakan yang tercatat adalah label kurir apa adanya
 * ("TIKI") — kabur, tapi tidak salah; itu justru lebih aman daripada salah.
 *
 * ⚠️ Hanya ekspedisi AKTIF yang dipertimbangkan, dan pencocokannya lewat KODE
 * — bukan menebak dari nama. Nama yang ditebak ("J&T" vs "JNT Express" vs
 * "J & T") adalah persis kerapuhan yang kolom kode itu dibuat untuk
 * menghilangkan. Tanpa padanan → `null`, dan pemanggil jatuh ke label kurir apa
 * adanya (nilai free-text tetap sah, lihat `buildExpeditionOptions`).
 */
export function expeditionNameForRate(
  list: ReadonlyArray<Expedition>,
  courierCode: string | null | undefined,
  serviceCode: string | null | undefined
): string | null {
  const code = normalizeCourierCode(courierCode);
  if (code === null) return null;
  const service = normalizeServiceCode(serviceCode);

  const sameCourier = list
    .filter(
      (e) =>
        e.isActive &&
        normalizeCourierCode(e.biteshipCourierCode) === code &&
        e.name.trim() !== "" &&
        // Baris DB bernama "Ambil di Tempat"/"Ongkir COD" TIDAK boleh menang.
        // Nama itu punya arti sendiri di hilir (`specialExpeditionKind` →
        // badge & perlakuan COD), jadi menuliskannya karena kebetulan kode
        // kurirnya cocok membuat kiriman biasa terbaca sebagai COD. Jatuh ke
        // `courierLabel()` ("JNE") jauh lebih aman daripada itu.
        !isSpecialExpedition(e.name)
    )
    .sort(
      (a, b) =>
        a.sortOrder - b.sortOrder ||
        a.name.localeCompare(b.name, "id", { sensitivity: "base" })
    );
  if (sameCourier.length === 0) return null;

  const exact =
    service === null
      ? undefined
      : sameCourier.find(
          (e) => normalizeServiceCode(e.biteshipServiceCode) === service
        );
  const generic = sameCourier.find(
    (e) => normalizeServiceCode(e.biteshipServiceCode) === null
  );

  return (exact ?? generic ?? sameCourier[0]).name.trim();
}

/**
 * Bentuk SATU-ARGUMEN `expeditionNameForRate` — pencocokan lewat kode kurir
 * saja, tanpa kode layanan.
 *
 * Dipertahankan untuk pemanggil yang memang hanya punya kode kurir. Jalur
 * pemilihan TARIF bukan salah satunya: `ShippingRate` selalu membawa
 * `serviceCode`, dan membuangnya di sana persis mengembalikan cacat yang mig
 * 3900 perbaiki.
 */
export function expeditionNameForCourier(
  list: ReadonlyArray<Expedition>,
  courierCode: string | null | undefined
): string | null {
  return expeditionNameForRate(list, courierCode, null);
}

/**
 * Kebalikan `expeditionNameForCourier`: nama ekspedisi (nilai tersimpan di
 * `shipment_requests.expedition`) → kode kurir Biteship, atau `null`.
 *
 * Dipakai untuk MENGURUTKAN daftar tarif: ekspedisi yang sudah dipilih naik ke
 * atas. Pencocokan nama di sini AMAN justru karena arahnya terbalik — yang
 * dicari adalah baris `expeditions` yang namanya persis sama, lalu kodenya
 * dibaca dari kolom. Tak ada tebakan "JNT ≈ J&T"; nama free-text lama yang tak
 * ada di daftar cukup memulangkan `null` (daftar tampil urut harga, apa adanya).
 *
 * Ekspedisi NONAKTIF ikut dicocokkan — nilai lama yang tersimpan di sebuah
 * kiriman tetap layak diurutkan ke atas walau adminnya sudah menonaktifkan
 * ekspedisi itu untuk kiriman baru.
 */
export function courierCodeForExpedition(
  list: ReadonlyArray<Expedition>,
  name: string | null | undefined
): string | null {
  const key = (name ?? "").trim().toLowerCase();
  if (key === "") return null;
  const match = list.find((e) => e.name.trim().toLowerCase() === key);
  return normalizeCourierCode(match?.biteshipCourierCode);
}

/**
 * Pindahkan ekspedisi pada `index` satu langkah ke atas/bawah dan kembalikan
 * daftar baru dengan `sortOrder` dinormalisasi = posisi indeksnya. Tak memutasi
 * input; bila pergerakan tak valid (di ujung) kembalikan daftar asal apa adanya.
 */
export function moveExpedition(
  list: Expedition[],
  index: number,
  dir: "up" | "down"
): Expedition[] {
  const target = dir === "up" ? index - 1 : index + 1;
  if (
    index < 0 ||
    index >= list.length ||
    target < 0 ||
    target >= list.length
  ) {
    return list;
  }
  return swapAndRenumber(list, index, target);
}

/**
 * Pindahkan ekspedisi (dikenali `id`) satu langkah ke atas/bawah DI DALAM
 * kategorinya sendiri, lalu normalisasi `sortOrder` = posisi indeks GLOBAL.
 * Tak memutasi input; bila tak ada tetangga sekategori ke arah itu, daftar
 * asal dikembalikan apa adanya.
 *
 * ── KENAPA BUKAN SEKADAR MEMATIKAN TOMBOL DI BATAS KATEGORI ────────────────
 *
 * `sort_order` tetap SATU daftar global — itu kolom yang sudah hidup, dipakai
 * juga `buildExpeditionOptions` dan `expeditionNameForRate`, dan membuatnya
 * per-kategori butuh migrasi sendiri. Tapi layarnya kini dua bagian, dan
 * kedua kategori BERSELANG-SELING di daftar global: di PROD, "NCS" (manual)
 * duduk di antara "LION REGPACK" dan "ANTER AJA" (keduanya otomatis).
 *
 * Kalau tombolnya sekadar dimatikan saat tetangga global-nya beda kategori,
 * pasangan itu jadi buntu permanen — daftar Otomatis tak bisa lagi diurutkan
 * melewati NCS yang bahkan tak terlihat di bagian itu. Karena itu yang ditukar
 * adalah tetangga SEKATEGORI berikutnya, seberapa jauh pun ia di daftar
 * global. Menukar dua baris sekategori tidak menggeser posisi baris kategori
 * lain sama sekali, jadi urutan bagian yang satunya tak pernah ikut berubah.
 */
export function moveExpeditionInGroup(
  list: Expedition[],
  id: string,
  dir: "up" | "down"
): Expedition[] {
  const index = list.findIndex((e) => e.id === id);
  if (index < 0) return list;
  // Baris bernama ekspedisi khusus tak berkategori (lihat `groupExpeditions`),
  // jadi ia tak punya bagian tempat ia bisa naik/turun.
  if (isSpecialExpedition(list[index].name)) return list;

  const kind = expeditionKind(list[index]);
  const step = dir === "up" ? -1 : 1;
  let target = index + step;
  while (
    target >= 0 &&
    target < list.length &&
    // ⚠️ Yang dilewati BUKAN cuma beda kategori: baris bentrok juga tak
    // tergambar di bagian mana pun, jadi menukarnya dengan itu membuat tombol
    // yang ditekan admin tampak tak melakukan apa-apa.
    (expeditionKind(list[target]) !== kind ||
      isSpecialExpedition(list[target].name))
  ) {
    target += step;
  }
  if (target < 0 || target >= list.length) return list;

  return swapAndRenumber(list, index, target);
}

/**
 * Tukar dua posisi lalu tulis ulang `sortOrder` = indeksnya. Primitif bersama
 * `moveExpedition` & `moveExpeditionInGroup` supaya keduanya tak bisa
 * berbeda soal apa arti "urutan tersimpan".
 */
function swapAndRenumber(
  list: Expedition[],
  a: number,
  b: number
): Expedition[] {
  const next = [...list];
  [next[a], next[b]] = [next[b], next[a]];
  return next.map((e, i) => ({ ...e, sortOrder: i }));
}


// ════════════════════════════════════════════════════════════════════════════
// Nama kurir yang sebenarnya BUKAN nama kurir
// ════════════════════════════════════════════════════════════════════════════

/**
 * Apa yang salah dengan sebuah nama ekspedisi yang diketik bebas — atau `null`
 * bila tak ada.
 *
 * ── KENAPA ADA (audit 2026-08-29) ──────────────────────────────────────────
 * Kolom nama kurir adalah SATU-SATUNYA teks bebas yang bisa dijangkau pembeli
 * di layar kirim, jadi ke situlah semua yang tak punya kolom sendiri berakhir.
 * Di PROD: 19 kiriman menyebut "resi"/"kode booking" di nama kurirnya padahal
 * `self_managed = false` dan `resi_number` KOSONG — nomornya ada, cuma di kolom
 * yang salah, sehingga label resi & pelacakan pembeli lahir kosong. Lima
 * lainnya menempelkan nominal ongkir ("JNT APLIKASI (ONGKIR : 9.600)") karena
 * sisi pembeli memang tak punya kolom untuk menyebut angka yang dikutip kurir.
 * Akibat keduanya sama: 92 nama kurir berbeda di `shipment_requests`, sehingga
 * Laporan Ekspedisi memecah satu J&T menjadi belasan "ekspedisi", dan teksnya
 * ikut tercetak apa adanya di label alamat.
 *
 * ── KENAPA BUKAN "MENGANDUNG ANGKA" ────────────────────────────────────────
 * Nama tier tarif yang SAH penuh angka: "JNT FLAT 12RB", "JNE JTR 150",
 * "TIKI T15", "JNT FLAT 25RB". Aturan naif akan menolak seluruh daftar kurir
 * milik toko. Karena itu dua lapis penjaga:
 *
 *  1. `knownNames` — nama yang sudah DIKURASI admin di Pengaturan Ekspedisi
 *     (plus dua ekspedisi khusus) selalu lolos, apa pun isinya. Itulah yang
 *     melindungi "NCS CARGO MANUAL ONGKIR" dan seluruh tier flat.
 *  2. Sisanya dinilai dari BENTUK, bukan dari ada-tidaknya angka:
 *     deret 8+ digit (nomor resi/kode booking tak pernah lebih pendek, nama
 *     kurir tak pernah sepanjang itu), dan angka ber-ribuan yang didahului
 *     "ongkir"/"Rp".
 *
 * `selfResiActive` mematikan lapis kata ("resi", "kode booking"): orang yang
 * SUDAH memakai panel resi sendiri boleh menamai kurirnya "RESI SHOPEE" —
 * ia sudah menaruh nomornya di kolom yang benar, dan namanya tinggal label.
 * Deret 8+ digit tetap ditolak: itu nomor resinya sendiri, disalin dua kali.
 */
export type ExpeditionNameProblem = {
  /** `resi` = nomor/kode di kolom nama; `ongkir` = nominal di kolom nama. */
  kind: "resi" | "ongkir";
  message: string;
};

/** Deret 8+ digit — nomor resi/kode booking, bukan nama kurir. */
const RESI_DIGITS_RE = /\d{8,}/;

/** Kata yang menandai "ini sebetulnya nomor resi", bukan nama kurir. */
const RESI_WORDS_RE = /\bresi\b|\bkode\s*booking\b|\bno\.?\s*resi\b/i;

/**
 * Nominal ber-ribuan yang didahului "ongkir"/"Rp" — mis. "ONGKIR : 9.600",
 * "Rp13.000", "ongkir 11.400".
 *
 * Sengaja MENUNTUT angka: "NCS CARGO MANUAL ONGKIR" dan "Ongkir COD" adalah
 * nama kurir sungguhan, dan menolaknya karena memuat kata "ongkir" berarti
 * menyuruh orang memindahkan sesuatu yang tak ada ke kolom lain.
 */
// ⚠️ TANPA `\b` di ujung kata kuncinya. "Rp13.000" ditulis TANPA spasi, dan
// batas kata di antara "p" dan "1" tidak ada — keduanya karakter kata. Menuntut
// batas di sana membuat justru bentuk yang paling sering diketik ("Rp13.000",
// "ncs darat Rp13.000") lolos tanpa tertangkap.
const ONGKIR_AMOUNT_RE =
  /\b(?:ongkir|rp)[^0-9a-z]{0,4}(?:\d{1,3}(?:[.,]\d{3})+|\d{3,})/i;

const normalizeName = (v: string | null | undefined): string =>
  (v ?? "").trim().toLowerCase().replace(/\s+/g, " ");

export function expeditionNameProblem(
  name: string | null | undefined,
  opts: {
    /** Nama ekspedisi terkurasi milik toko; ekspedisi khusus selalu ikut. */
    knownNames?: readonly string[];
    /** Panel "Punya Resi Sendiri" sedang menyala? */
    selfResiActive?: boolean;
  } = {}
): ExpeditionNameProblem | null {
  const raw = (name ?? "").trim();
  if (raw === "") return null;

  const known = new Set(
    [...SPECIAL_EXPEDITIONS, ...(opts.knownNames ?? [])].map(normalizeName)
  );
  if (known.has(normalizeName(raw))) return null;

  if (RESI_DIGITS_RE.test(raw)) {
    return {
      kind: "resi",
      message: opts.selfResiActive
        ? "Nomor resinya sudah punya kolom sendiri di bawah — di sini cukup nama kurirnya saja (mis. “J&T”, “SPX”)."
        : "Itu nomor resi/kode booking, bukan nama kurir. Pakai “Punya Resi Sendiri” supaya nomornya tercatat & tercetak di label."
    };
  }

  if (!opts.selfResiActive && RESI_WORDS_RE.test(raw)) {
    return {
      kind: "resi",
      message:
        "Sudah punya resi sendiri? Pakai “Punya Resi Sendiri” — nomornya tercatat dan tercetak di label. Kolom ini cuma nama kurirnya (mis. “J&T”, “SPX”)."
    };
  }

  if (ONGKIR_AMOUNT_RE.test(raw)) {
    return {
      kind: "ongkir",
      message:
        "Nominal ongkir jangan ditulis di nama kurir — teksnya ikut tercetak di label dan memecah Laporan Ekspedisi. Tulis angkanya di kolom ongkir."
    };
  }

  return null;
}
